import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { NAV } from "@/lib/content";
import { Logo } from "@/components/Logo";
import { EASE } from "@/components/motion/Reveal";
import { useSettings } from "@/hooks/useData";
import { useIntro } from "@/components/Intro";

const DARK_HERO = ["/", "/leistungen", "/privatreinigung", "/umzugsreinigung", "/baureinigung", "/fensterreinigung", "/gartenbau-pflege", "/geschaeftskunden", "/ueber-uns"];
const isDarkHero = (p) => DARK_HERO.includes(p) || p.startsWith("/leistungen/") || p.startsWith("/standorte/");

export function Nav() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const settings = useSettings();
  const { done } = useIntro();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (window.__lenis) open ? window.__lenis.stop() : window.__lenis.start();
  }, [open]);

  const onDark = isDarkHero(pathname) && !scrolled && !open;
  const text = onDark ? "text-paper" : "text-ink";

  return (
    <>
      <motion.header
        data-testid="site-nav"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-700 ease-out ${
          scrolled && !open ? "bg-paper/85 backdrop-blur-xl border-b border-line" : "border-b border-transparent"
        }`}
        initial={{ y: -20, opacity: 0 }}
        animate={done ? { y: 0, opacity: 1 } : undefined}
        transition={{ duration: 1, ease: EASE, delay: 0.2 }}
      >
        <div className={`container-x flex items-center justify-between transition-[height] duration-700 ease-out ${scrolled ? "h-16" : "h-20 md:h-24"}`}>
          <Logo compact={scrolled} light={onDark || open} />
          <nav aria-label="Hauptnavigation" className={`hidden lg:flex items-center gap-10 ${text} transition-colors duration-500`}>
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.id}`}
                className={({ isActive }) => `label link-line py-2 ${isActive ? "after:scale-x-100" : ""}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <Link
              to="/kontakt"
              data-testid="nav-cta-offerte"
              className={`hidden md:inline-flex label px-6 py-3.5 border transition-colors duration-500 ease-out ${
                onDark ? "border-paper/50 text-paper hover:bg-paper hover:text-ink" : "border-ink text-ink hover:bg-ink hover:text-paper"
              }`}
            >
              Offerte anfragen
            </Link>
            <button
              type="button"
              data-testid="nav-menu-toggle"
              aria-label={open ? "Menü schliessen" : "Menü öffnen"}
              aria-expanded={open}
              onClick={() => setOpen((o) => !o)}
              className={`lg:hidden relative w-11 h-11 -mr-2 flex items-center justify-center ${open ? "text-paper" : text}`}
            >
              <span className={`absolute block h-px w-6 bg-current transition-transform duration-500 ease-out ${open ? "rotate-45" : "-translate-y-[3px]"}`} />
              <span className={`absolute block h-px w-6 bg-current transition-transform duration-500 ease-out ${open ? "-rotate-45" : "translate-y-[3px]"}`} />
            </button>
          </div>
        </div>
      </motion.header>
      <MobileMenu open={open} settings={settings} />
    </>
  );
}

function MobileMenu({ open, settings }) {
  const reduced = useReducedMotion();
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="mobile-menu"
          className="fixed inset-0 z-40 bg-ink text-paper grain flex flex-col"
          initial={reduced ? { opacity: 0 } : { clipPath: "inset(0 0 100% 0)" }}
          animate={reduced ? { opacity: 1 } : { clipPath: "inset(0 0 0% 0)" }}
          exit={reduced ? { opacity: 0 } : { clipPath: "inset(0 0 100% 0)" }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <div className="container-x flex-1 flex flex-col justify-end pb-28 pt-28 relative z-10">
            <img src="/logo-white.png" alt="Emini Reinigung" className="absolute top-6 left-[clamp(20px,5vw,96px)] h-9 w-auto" />
            <nav aria-label="Mobile Navigation" className="flex flex-col">
              {NAV.map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={{ y: 40, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.9, ease: EASE, delay: 0.25 + i * 0.06 }}
                  className="border-t border-paper/15"
                >
                  <Link to={n.to} data-testid={`mobile-nav-link-${n.id}`} className="flex items-baseline justify-between py-5">
                    <span className="display text-4xl sm:text-5xl">{n.label}</span>
                    <span className="label text-paper/40">0{i + 1}</span>
                  </Link>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-10 flex flex-col gap-3 label text-paper/60"
            >
              <a href={`tel:${settings?.phone_link || ""}`} className="link-line w-max text-paper">{settings?.phone || "Telefon"}</a>
              <a href={`mailto:${settings?.email || ""}`} className="link-line w-max text-paper">{settings?.email || "E-Mail"}</a>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
